import type { CSSProperties, ReactNode } from 'react';
import { Flex, Typography, theme } from 'antd';
import CardShellEcho from './CardShellEcho';

interface MetricCardEchoProps {
  className?: string;
  style?: CSSProperties;
  testId?: string;
  dataTestId?: string;
  title?: ReactNode;
  body?: ReactNode;
  footer?: ReactNode;
}

/**
 * Card shell + title/body/footer typography and spacing for Echo metric cards, styled to match
 * `UniquesCardEcho`. `testId` targets the outer card root; `dataTestId` targets the inner data
 * wrapper. Each of `title`/`body`/`footer` accepts a plain string (gets the standard styling) or
 * a `ReactNode` (rendered as-is, for callers composing their own tooltip/icon/badge rows).
 */
const MetricCardEcho = ({
  className,
  style,
  testId,
  dataTestId,
  title,
  body,
  footer,
}: MetricCardEchoProps) => {
  const { token } = theme.useToken();

  return (
    <CardShellEcho className={className} style={style} testId={testId}>
      <Flex vertical gap={token.marginXXS} data-testid={dataTestId}>
        {title != null &&
          (typeof title === 'string' ? (
            <Flex align="center" style={{ minHeight: token.controlHeightSM }}>
              <Typography.Text style={{ color: token.colorTextSecondary }}>{title}</Typography.Text>
            </Flex>
          ) : (
            title
          ))}
        {body != null &&
          (typeof body === 'string' || typeof body === 'number' ? (
            <Typography.Title level={3} ellipsis={{ rows: 1 }} style={{ margin: 0 }}>
              {body}
            </Typography.Title>
          ) : (
            body
          ))}
        {footer != null &&
          (typeof footer === 'string' ? (
            <Typography.Text
              style={{
                color: token.colorTextSecondary,
                fontSize: token.fontSizeSM,
              }}
            >
              {footer}
            </Typography.Text>
          ) : (
            footer
          ))}
      </Flex>
    </CardShellEcho>
  );
};

export default MetricCardEcho;
